import Grade from "@components/grade";
import {
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonNote,
} from "@ionic/react";
import { useDueCount } from "@lib/fsrs/dueCounts";
import { IDeck } from "@models/deck";
import { isDeckFSRS } from "@models/deckSettings";
import DeckDownloadScreenFC from "@screens/deckDownload";
import { superCache } from "hooks/data/superCache";
import { useIonCardModalWithDismiss } from "hooks/util/useIonModalWithDismiss";
import { checkmarkCircleOutline, trash } from "ionicons/icons";
import L10n from "localization";
import { useMemo } from "react";
import { IoCloudOutline } from "react-icons/io5";
import { useHistory } from "react-router";
import Style from "style";
import { useGradePercent } from "./useGradePercent";

interface IStudyGroupDeckItemProps {
  key: string;
  deck: IDeck;
  name: string;
  type: string;
  href?: string;
  onRemoveDeck?: (deckId: string) => void;
}

export default function StudyGroupDeckItem(props: IStudyGroupDeckItemProps): JSX.Element {
  const { name, href, type, onRemoveDeck, deck } = props;

  // Get full deck info from superCache which includes user_config
  const fullDeck = superCache.decks?.get(deck.id) || deck;
  const decks = useMemo(() => [fullDeck], [fullDeck]);
  const isFSRS = isDeckFSRS(fullDeck);
  const gradePct = useGradePercent(decks, false);
  const lensSpec = useMemo(() => ({ deckID: deck.id }), [deck.id]);
  const isNonLocal = !["local", "group", "shared-local"].includes(type);
  const rawDueCount = useDueCount(lensSpec);
  const dueCount = isNonLocal ? undefined : rawDueCount;

  const history = useHistory();
  const [presentDownloadDeckModal] = useIonCardModalWithDismiss(DeckDownloadScreenFC, {
    deckID: deck.id,
    history: history,
  });

  return (
    <IonItemSliding>
      <IonItem
        detail
        onClick={() => {
          if (isNonLocal) {
            presentDownloadDeckModal();
          } else if (href) {
            history.push(href);
          }
        }}
        button
      >
        <IonLabel>{name}</IonLabel>
        <IonNote slot="end">
          {isNonLocal ? (
            <IoCloudOutline size={24} opacity={0.5} />
          ) : isFSRS ? (
            dueCount === 0 ? (
              <IonIcon
                icon={checkmarkCircleOutline}
                style={{
                  fontSize: "1.5rem",
                  color: Style.colors.easy,
                }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  fontSize: "1.25rem",
                  color: Style.colors.grades.F,
                }}
              >
                <div style={{ color: Style.colors.grades.F }}>{dueCount}</div>
                <div style={{ fontSize: "0.75rem", opacity: 0.6 }}>
                  {L10n.localize((s) => s.fsrs.dueTitle).toLocaleLowerCase()}
                </div>
              </div>
            )
          ) : (
            <Grade percent={gradePct} fontSize="1rem" showPercent={true} />
          )}
        </IonNote>
      </IonItem>
      {onRemoveDeck && (
        <IonItemOptions side="end">
          <IonItemOption color="danger" onClick={() => onRemoveDeck(deck.id)}>
            <IonIcon slot="icon-only" icon={trash} />
          </IonItemOption>
        </IonItemOptions>
      )}
    </IonItemSliding>
  );
}
